import { z } from 'zod';
import * as s from './schemas.ts';

/**
 * Typed catalog of PostgREST RPC operations. `args` are the SQL parameter names
 * (PostgREST named arguments); `result` parses the JSON response.
 */
export const RPC = {
  get_me: { result: s.meSchema },
  complete_onboarding: { result: z.object({ ok: z.literal(true), instrumentProfileId: z.uuid().nullable() }) },
  update_profile: { result: z.object({ ok: z.literal(true) }) },
  save_instrument_profile: { result: z.uuid() },
  set_active_instrument: { result: z.null().or(z.undefined()).or(z.literal('')) },
  archive_instrument_profile: { result: z.unknown() },
  mark_notifications_read: { result: z.unknown() },
  log_product_event: { result: z.unknown() },
  count_guest_event: { result: z.unknown() },
  get_global_leaderboard: { result: s.globalLeaderboardSchema },

  get_student_home: { result: s.studentHomeSchema },
  get_path_progress: { result: s.pathProgressSchema },
  get_skill_progress: { result: s.skillProgressSchema },
  get_activity_history: { result: s.activityHistorySchema },
  get_rewards: { result: s.rewardsSchema },
  get_my_classes: { result: s.myClassesSchema },
  set_placement_start: { result: z.unknown() },
  join_classroom: { result: s.joinResultSchema },
  leave_classroom: { result: z.unknown() },
  get_leaderboard: { result: s.leaderboardSchema },

  get_session: { result: s.sessionStateSchema },
  submit_answer_event: { result: s.answerEventResultSchema },
  finalize_session: { result: s.finalizeResultSchema },
  abandon_session: { result: z.unknown() },

  get_teacher_dashboard: { result: s.teacherDashboardSchema },
  create_classroom: { result: s.createClassroomResultSchema },
  update_classroom: { result: z.unknown() },
  set_classroom_archived: { result: z.unknown() },
  rotate_invite_code: { result: z.object({ code: z.string(), expiresAt: z.string().nullable() }) },
  revoke_invite_code: { result: z.unknown() },
  remove_member: { result: z.unknown() },
  restore_member: { result: z.unknown() },
  get_classroom: { result: s.classroomDetailSchema },
  create_plan: { result: z.uuid() },
  update_plan: { result: z.unknown() },
  get_plan: { result: s.planDetailSchema },
  save_activity: { result: z.uuid() },
  delete_activity: { result: z.unknown() },
  move_activity: { result: z.unknown() },
  archive_plan: { result: z.unknown() },
  assign_to_new_students: { result: z.number().int() },
  list_plan_templates: { result: s.templatesSchema },
  save_plan_as_template: { result: z.uuid() },
  delete_template: { result: z.unknown() },
  get_assignment_report: { result: s.assignmentReportSchema },
  get_student_report: { result: s.studentReportSchema },
  get_plan_results_export: { result: s.exportRowsSchema },
} as const;

export type RpcName = keyof typeof RPC;
export type RpcResult<N extends RpcName> = z.infer<(typeof RPC)[N]['result']>;

type Uuid = string;

/** Anonymous daily counts for signed-out visitors (SQL: count_guest_event). */
export type GuestEventName = 'front_page_visit' | 'signup_page_visit' | 'guest_practice_started' | 'guest_practice_completed' | 'signup_prompt_clicked';
/** The detail each guest event allows; the SQL function rejects anything else. */
export type GuestEventDetail<N extends GuestEventName> = N extends 'guest_practice_started' | 'guest_practice_completed'
  ? 'notes' | 'rhythm'
  : N extends 'signup_prompt_clicked'
    ? 'header' | 'front-page' | 'practice-page' | 'summary' | 'footer'
    : '';

/** Parameter names/types for each RPC (validated again in SQL). */
export interface RpcArgs {
  get_me: Record<string, never>;
  complete_onboarding: {
    p_display_name: string;
    p_timezone: string;
    p_wants_student: boolean;
    p_wants_teacher: boolean;
    p_experience_level: string;
    p_daily_goal_minutes: number;
    p_instrument: s.InstrumentInput | null;
  };
  update_profile: { p_patch: s.ProfilePatch };
  save_instrument_profile: { p_id: Uuid | null; p_data: s.InstrumentInput; p_make_active: boolean };
  set_active_instrument: { p_id: Uuid };
  archive_instrument_profile: { p_id: Uuid };
  mark_notifications_read: { p_ids: Uuid[] | null };
  log_product_event: { p_name: 'lesson_started'; p_properties: Record<string, unknown> } | { p_name: 'onboarding_completed'; p_properties: { afterGuestPractice: boolean } };
  get_global_leaderboard: { p_period: 'all-time'; p_limit: number };
  count_guest_event: { [N in GuestEventName]: { p_name: N; p_detail: GuestEventDetail<N> } }[GuestEventName];

  get_student_home: Record<string, never>;
  get_path_progress: { p_clef: string };
  get_skill_progress: Record<string, never>;
  get_activity_history: { p_limit: number; p_before: string | null };
  get_rewards: Record<string, never>;
  get_my_classes: Record<string, never>;
  set_placement_start: { p_clef: string };
  join_classroom: { p_code: string };
  leave_classroom: { p_classroom_id: Uuid };
  get_leaderboard: { p_classroom_id: Uuid };

  get_session: { p_session_id: Uuid };
  submit_answer_event: {
    p_event_id: Uuid;
    p_session_id: Uuid;
    p_question_id: Uuid;
    p_kind: 'answer' | 'hint' | 'reveal';
    p_answer: s.AnswerValue | s.RhythmAnswerValue | null;
    p_hint_level: number | null;
    p_response_ms: number | null;
    p_client_created_at: string | null;
  };
  finalize_session: { p_session_id: Uuid };
  abandon_session: { p_session_id: Uuid };

  get_teacher_dashboard: Record<string, never>;
  create_classroom: { p_name: string; p_description: string; p_timezone: string; p_leaderboard_enabled: boolean };
  update_classroom: { p_classroom_id: Uuid; p_patch: Partial<{ name: string; description: string; timezone: string; leaderboard_enabled: boolean }> };
  set_classroom_archived: { p_classroom_id: Uuid; p_archived: boolean };
  rotate_invite_code: { p_classroom_id: Uuid; p_expires_at: string | null };
  revoke_invite_code: { p_classroom_id: Uuid };
  remove_member: { p_classroom_id: Uuid; p_student_id: Uuid };
  restore_member: { p_classroom_id: Uuid; p_student_id: Uuid };
  get_classroom: { p_classroom_id: Uuid };
  create_plan: { p_classroom_id: Uuid; p_title: string; p_week_start: string; p_source_kind: 'blank' | 'template' | 'plan'; p_source_id: Uuid | null };
  update_plan: { p_plan_id: Uuid; p_patch: Partial<{ title: string; week_start: string; assignment_focused: boolean }> };
  get_plan: { p_plan_id: Uuid };
  save_activity: { p_plan_id: Uuid; p_activity: s.ActivityInput };
  delete_activity: { p_activity_id: Uuid };
  move_activity: { p_activity_id: Uuid; p_direction: -1 | 1 };
  archive_plan: { p_plan_id: Uuid };
  assign_to_new_students: { p_assignment_id: Uuid };
  list_plan_templates: Record<string, never>;
  save_plan_as_template: { p_plan_id: Uuid; p_title: string };
  delete_template: { p_template_id: Uuid };
  get_assignment_report: { p_assignment_id: Uuid };
  get_student_report: { p_classroom_id: Uuid; p_student_id: Uuid };
  get_plan_results_export: { p_plan_id: Uuid };
}

/** Edge Functions and their request/response schemas. */
export const FUNCTIONS = {
  'session-start': { request: s.sessionStartRequestSchema, response: s.sessionStartResponseSchema },
  'plan-publish': { request: s.publishRequestSchema, response: s.publishResponseSchema },
} as const;
export type FunctionName = keyof typeof FUNCTIONS;

/** Parse an RPC response with its contract schema (typed by operation name). */
export function parseRpcResult<N extends RpcName>(name: N, data: unknown): RpcResult<N> {
  return (RPC[name].result as unknown as z.ZodType<RpcResult<N>>).parse(data);
}

/** Like parseRpcResult but returns a Zod result instead of throwing. */
export function safeParseRpcResult<N extends RpcName>(name: N, data: unknown) {
  return (RPC[name].result as unknown as z.ZodType<RpcResult<N>>).safeParse(data);
}
